import React, { useState, useEffect } from 'react';
import manualUpdateAPI from '../../services/manualUpdateAPI';
import containerAPI from '../../services/containerAPI'; // Fix import
import { handleApiError, formatDateTime } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import QuickContainerModal from './QuickContainerModal'; // Add this import
import { validateContainerNumber, calculateCheckDigit } from '../../utils/ContainerNumberValidator';

const ManualUpdateForm = ({ agent, onUpdate, statusMappings }) => {
  // Port details state
  const [portOptions, setPortOptions] = useState([]);

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const response = await containerAPI.getPorts();
        setPortOptions(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        setPortOptions([]);
      }
    };
    fetchPorts();
  }, []);
  const [formData, setFormData] = useState({
    containerNumber: '',
    carrierName: '',
    carrierStatusCode: '',
    eventTime: new Date().toISOString().slice(0, 10), // yyyy-MM-dd
    location: '',
    notes: ''
  });
  // New state for container status options from DB
  const [containerStatusOptions, setContainerStatusOptions] = useState([]);
  // Fetch container status options from backend when carrier changes
  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const response = await containerAPI.getStatusOptions();
        setContainerStatusOptions(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        setContainerStatusOptions([]);
      }
    };
    fetchStatusOptions();
  }, []); 
  // statusMappings now comes from props 
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [containerExists, setContainerExists] = useState(null);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [checkingContainer, setCheckingContainer] = useState(false);
  const [locationError, setLocationError] = useState('');

  useEffect(() => {
    // Validate container number before checking existence
    if (!formData.containerNumber || !validateContainerNumber(formData.containerNumber)) {
      setContainerExists(null);
      setCheckingContainer(false);
      return;
    }
    const checkContainer = async () => {
      if (formData.containerNumber && formData.containerNumber.length >= 4) {
        setCheckingContainer(true);
        try {
          await containerAPI.getByNumber(formData.containerNumber);
          setContainerExists(true);
        } catch (error) {
          setContainerExists(false);
        } finally {
          setCheckingContainer(false);
        }
      } else {
        setContainerExists(null);
      }
    };
    const timeoutId = setTimeout(checkContainer, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.containerNumber]);

  // Remove local statusMappings loading, use prop

  // Auto-populate check digit when 10 chars entered
  useEffect(() => {
    const num = formData.containerNumber;
    // Only auto-populate if user hasn't already entered a check digit
    if (num.length === 10 && /^[A-Z]{4}[0-9]{6}$/.test(num)) {
      const digit = calculateCheckDigit(num);
      if (digit) {
        setFormData(prev => {
          // Only append if not already present
          if (prev.containerNumber.length === 10) {
            return {
              ...prev,
              containerNumber: num + digit
            };
          }
          return prev;
        });
      }
    }
  }, [formData.containerNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if container exists before submitting
    if (containerExists === false) {
      setError('Container not found. Please create the container first.');
      setShowQuickCreate(true);
      return;
    }

    // Validate container number format
    if (!validateContainerNumber(formData.containerNumber)) {
      setError('Invalid container number format or check digit. Please check the owner code, product group code, and check digit.');
      return;
    }

    // Validate location before submit
    const validPort = portOptions.some(port => port.portCode === formData.location);
    if (!validPort) {
      setLocationError('Invalid port code. Please select a port from the list.');
      return;
    }

    setLoading(true);
    setMessage('');
    setError('');

    try {
      // Send portCode for location
      const payload = {
        ...formData,
        location: formData.location,
        eventTime: formData.eventTime // already yyyy-MM-dd
      };
      await manualUpdateAPI.createManualUpdate(payload);

      setMessage('Status updated successfully!');
      setFormData({
        containerNumber: formData.containerNumber, // Keep container number for quick consecutive updates
        carrierName: '',
        carrierStatusCode: '',
        eventTime: new Date().toISOString().slice(0, 10), // yyyy-MM-dd
        location: '',
        notes: ''
      });

      // Notify parent to refresh data
      if (onUpdate) onUpdate();

    } catch (error) {
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Location validation
    if (name === 'location') {
      const validPort = portOptions.some(port => port.portCode === value);
      setLocationError(validPort || value === '' ? '' : 'Invalid port code. Please select a port from the list.');
    }
  };

  const handleContainerCreated = () => {
    setShowQuickCreate(false);
    setContainerExists(true);
    setMessage('Container created successfully! You can now update its status.');
  };

  const currentMappings = statusMappings[formData.carrierName] || [];

  return (
    <div className="manual-update-form">
      <div className="form-header">
        <h3>Manual Status Update</h3>
        <p>Update container status based on carrier website information</p>
      </div>

      <div className="agent-context">
        <strong>Updating as:</strong> {agent.fullName} ({agent.role})
      </div>

      {message && (
        <div className="alert alert-success">
          {message}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="update-form">
        <div className="form-section">
          <h4>Container Information</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Container Number *</label>
              <input
                type="text"
                name="containerNumber"
                value={formData.containerNumber}
                onChange={handleChange}
                required
                placeholder="e.g., CMAU1234567"
                disabled={loading}
                className={containerExists === false ? 'input-error' : ''}
              />
              <div className="input-status">
                {checkingContainer && formData.containerNumber.length >= 4 && (
                  <span className="checking">Checking container...</span>
                )}
                {!checkingContainer && containerExists === true && (
                  <span className="exists">✓ Container exists</span>
                )}
                {!checkingContainer && containerExists === false && (
                  <span className="not-exists">
                    ✗ Container not found. 
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => setShowQuickCreate(true)}
                    >
                      Create it now
                    </button>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Carrier Information</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Carrier Source *</label>
              <input
                type="text"
                name="carrierName"
                value={formData.carrierName}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Enter Carrier Name"
              />
            </div>

            <div className="form-group">
              <label>Carrier Status *</label>
              <select
                name="carrierStatusCode"
                value={formData.carrierStatusCode}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="">Select Status</option>
                {containerStatusOptions.map((option, idx) => (
                  <option key={option.statusId ?? option.id ?? idx} value={option.carrierStatusCode ?? option.statusDesc}>
                    {option.carrierStatusText ?? option.statusDesc}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Event Details</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Event Date *</label>
              <input
                type="date"
                name="eventTime"
                value={formData.eventTime}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Location *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                disabled={loading}
                list="port-list"
                placeholder="Type or select port..."
                className={locationError ? 'input-error' : ''}
              />
              <datalist id="port-list">
                {portOptions.map(port => (
                  <option key={port.portId} value={port.portCode}>
                    {port.portName} ({port.portCode})
                  </option>
                ))}
              </datalist>
              {locationError && <span style={{ color: 'red', fontSize: '0.9em' }}>{locationError}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              disabled={loading}
              placeholder="Any additional information from carrier website..."
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || containerExists === false}
          >
            {loading ? <LoadingSpinner size="small" text="" /> : 'Update Container Status'}
          </button>
        </div>
      </form>

      {/* Quick Container Creation Modal */}
      {showQuickCreate && (
        <QuickContainerModal
          containerNumber={formData.containerNumber}
          onSuccess={handleContainerCreated}
          onCancel={() => setShowQuickCreate(false)}
        />
      )}

      <style>{`
        .manual-update-form {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          padding: 2rem;
        }
        
        .form-header {
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #eee;
        }
        
        .form-header h3 {
          margin: 0 0 0.5rem 0;
          color: #333;
        }
        
        .form-header p {
          margin: 0;
          color: #666;
        }
        
        .agent-context {
          background: #e7f3ff;
          padding: 0.75rem;
          border-radius: 4px;
          margin-bottom: 1.5rem;
          color: #0066cc;
          font-size: 0.9rem;
        }
        
        .form-section {
          margin-bottom: 2rem;
        }
        
        .form-section h4 {
          margin: 0 0 1rem 0;
          color: #555;
          font-size: 1.1rem;
        }
        
        .input-status {
          margin-top: 0.25rem;
          font-size: 0.8rem;
          min-height: 1.2rem;
        }
        
        .checking {
          color: #666;
          font-style: italic;
        }
        
        .exists {
          color: #059669;
        }
        
        .not-exists {
          color: #dc2626;
        }
        
        .input-error {
          border-color: #dc2626 !important;
        }
        
        .btn-link {
          background: none;
          border: none;
          color: #0066cc;
          text-decoration: underline;
          cursor: pointer;
          margin-left: 0.5rem;
        }
        
        .btn-link:hover {
          color: #0052a3;
        }
        
        .form-actions {
          display: flex;
          justify-content: center;
          align-items: center;
          padding-top: 1rem;
          border-top: 1px solid #eee;
        }
        
        .form-actions .btn {
          min-width: 200px;
        }
        
        @media (max-width: 768px) {
          .manual-update-form {
            padding: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ManualUpdateForm;